import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal, Table, Badge } from 'react-bootstrap';
import { FaCalculator, FaFileContract, FaSearch, FaEdit } from 'react-icons/fa';

import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { validateTCNumber, validatePhoneNumber } from '../utils/validators';

// VKN validation
const validateVKNNumber = (vknNo) => {
    if (!vknNo || typeof vknNo !== 'string') return false;
    const cleanVknNo = vknNo.replace(/\D/g, '');
    return cleanVknNo.length === 10 && cleanVknNo[0] !== '0';
};

const StandaloneSenetPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [identityError, setIdentityError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [plateSearchError, setPlateSearchError] = useState('');
    const [searchingByPlate, setSearchingByPlate] = useState(false);
    const [foundVehicle, setFoundVehicle] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);


    // Customer Data
    const [customerData, setCustomerData] = useState({
        name: '',
        phone: '',
        email: '',
        tcNo: '',
        address: ''
    });

    const [identityType, setIdentityType] = useState('TCKN');

    // Guarantor Data (optional)
    const [guarantorData, setGuarantorData] = useState({
        name: '',
        tcNo: '',
        address: '',
        identityType: 'TCKN'
    });
    const [hasGuarantor, setHasGuarantor] = useState(false);
    const [guarantorError, setGuarantorError] = useState('');

    // Vehicle Data
    const [vehicleData, setVehicleData] = useState({
        make: '',
        model: '',
        price: 0,
        plate: ''
    });

    // Financial Data
    const [financialData, setFinancialData] = useState({
        downPayment: 0,
        interestRate: 7.0,
        term: 1
    });

    // Payment Plan
    const [paymentPlan, setPaymentPlan] = useState([]);
    const [senetBaslangicTarihi, setSenetBaslangicTarihi] = useState('');

    // Formatlanmış değerler
    const [formattedPrice, setFormattedPrice] = useState('0');
    const [formattedDownPayment, setFormattedDownPayment] = useState('0');

    // Helper functions
    const formatNumber = (num) => {
        if (!num || num === 0) return '0';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    const parseFormattedNumber = (str) => {
        if (!str || str === '') return 0;
        const cleaned = str.replace(/\./g, '');
        return parseInt(cleaned, 10) || 0;
    };

    // PMT calculation
    const calculatePMT = (rate, nper, pv) => {
        if (rate === 0) return pv / nper;
        const pvif = Math.pow(1 + rate, nper);
        const pmt = (rate * pv * pvif) / (pvif - 1);
        return pmt;
    };

    // Generate payment plan
    const generatePaymentPlan = () => {
        const loanAmount = vehicleData.price - financialData.downPayment;
        if (loanAmount <= 0 || financialData.term <= 0) return [];

        const monthlyInterestRate = financialData.interestRate / 100;
        const monthlyPayment = calculatePMT(monthlyInterestRate, financialData.term, loanAmount);

        const plan = [];
        const startDate = new Date(senetBaslangicTarihi || new Date());

        for (let i = 1; i <= financialData.term; i++) {
            const dueDate = new Date(startDate);
            dueDate.setMonth(dueDate.getMonth() + (i - 1));

            // Weekend check
            if (dueDate.getDay() === 0) dueDate.setDate(dueDate.getDate() + 1);
            else if (dueDate.getDay() === 6) dueDate.setDate(dueDate.getDate() + 2);

            plan.push({
                taksit: i,
                taksitTarihi: dueDate.toISOString().split('T')[0],
                taksitTutari: Math.round(monthlyPayment),
                paymentStatus: 'pending'
            });
        }

        return plan;
    };

    // Set default senet date
    useEffect(() => {
        const defaultDate = new Date();
        defaultDate.setMonth(defaultDate.getMonth() + 1);
        setSenetBaslangicTarihi(defaultDate.toISOString().split('T')[0]);
    }, []);

    // Search vehicle by plate
    const handleSearchByPlate = async () => {
        if (!vehicleData.plate || vehicleData.plate.length < 5) {
            setPlateSearchError('Geçerli bir plaka numarası giriniz');
            return;
        }

        setSearchingByPlate(true);
        setPlateSearchError('');

        try {
            const response = await api.get(`/vehicles/search/plate/${vehicleData.plate}`);

            if (response.data.success && response.data.vehicles.length > 0) {
                const vehicle = response.data.vehicles[0];
                setFoundVehicle(vehicle);

                setVehicleData(prev => ({
                    ...prev,
                    make: vehicle.brand || prev.make,
                    model: vehicle.model || prev.model,
                    price: vehicle.salePrice || vehicle.purchasePrice || prev.price
                }));

                if (vehicle.salePrice || vehicle.purchasePrice) {
                    setFormattedPrice(formatNumber(vehicle.salePrice || vehicle.purchasePrice));
                }

                setSuccess(`🚗 ${vehicle.brand} ${vehicle.model} aracı bulundu!`);
            } else {
                setPlateSearchError('Bu plaka numarasına ait araç bulunamadı');
                setFoundVehicle(null);
            }
        } catch (error) {
            console.error('Araç arama hatası:', error);
            setPlateSearchError('Araç aranırken hata oluştu');
            setFoundVehicle(null);
        } finally {
            setSearchingByPlate(false);
        }
    };

    // Handle customer data change
    const handleCustomerChange = (field, value) => {
        const upperCaseFields = ['name', 'address'];
        if (upperCaseFields.includes(field)) {
            value = value.toUpperCase();
        }

        if (field === 'tcNo') {
            if (value && !/^[0-9]+$/.test(value)) return;
            setIdentityError('');
            const maxLength = identityType === 'TCKN' ? 11 : 10;
            if (value.length > maxLength) return;

            if (value.length === maxLength) {
                if (identityType === 'TCKN') {
                    if (!validateTCNumber(value)) {
                        setIdentityError('Geçersiz T.C. Kimlik Numarası');
                    }
                } else if (identityType === 'VKN') {
                    if (!validateVKNNumber(value)) {
                        setIdentityError('Geçersiz Vergi Kimlik Numarası');
                    }
                }
            }
        }

        if (field === 'phone') {
            if (value && !/^[0-9+\s\-\(\)]+$/.test(value)) return;
            setPhoneError('');
            if (value.length >= 10) {
                if (!validatePhoneNumber(value)) {
                    setPhoneError('Geçerli bir telefon numarası giriniz');
                }
            }
        }

        setCustomerData(prev => ({ ...prev, [field]: value }));
    };

    // Handle guarantor data change
    const handleGuarantorChange = (field, value) => {
        const upperCaseFields = ['name', 'address'];
        if (upperCaseFields.includes(field)) {
            value = value.toUpperCase();
        }

        if (field === 'tcNo') {
            if (value && !/^[0-9]+$/.test(value)) return;
            setGuarantorError('');
            const maxLength = guarantorData.identityType === 'TCKN' ? 11 : 10;
            if (value.length > maxLength) return;

            if (value.length === maxLength) {
                if (guarantorData.identityType === 'TCKN') {
                    if (!validateTCNumber(value)) {
                        setGuarantorError('Geçersiz T.C. Kimlik Numarası');
                    }
                } else if (guarantorData.identityType === 'VKN') {
                    if (!validateVKNNumber(value)) {
                        setGuarantorError('Geçersiz Vergi Kimlik Numarası');
                    }
                }
            }
        }

        setGuarantorData(prev => ({ ...prev, [field]: value }));
    };

    // Handle vehicle data change
    const handleVehicleChange = (field, value) => {
        const upperCaseFields = ['make', 'model', 'plate'];
        if (upperCaseFields.includes(field)) {
            value = value.toUpperCase();
        }

        if (field === 'plate') {
            setFoundVehicle(null);
            setPlateSearchError('');
        }

        setVehicleData(prev => ({ ...prev, [field]: value }));
    };

    // Handle formatted number change
    const handleFormattedNumberChange = (field, value) => {
        const cleanValue = value.replace(/[^\d]/g, '');

        if (cleanValue === '') {
            if (field === 'price') {
                setFormattedPrice('0');
                setVehicleData(prev => ({ ...prev, price: 0 }));
            } else if (field === 'downPayment') {
                setFormattedDownPayment('0');
                setFinancialData(prev => ({ ...prev, downPayment: 0 }));
            }
            return;
        }

        const numericValue = parseInt(cleanValue, 10);
        const formatted = formatNumber(numericValue);

        if (field === 'price') {
            setFormattedPrice(formatted);
            setVehicleData(prev => ({ ...prev, price: numericValue }));
        } else if (field === 'downPayment') {
            setFormattedDownPayment(formatted);
            setFinancialData(prev => ({ ...prev, downPayment: numericValue }));
        }
    };

    // Handle financial data change
    const handleFinancialChange = (field, value) => {
        if (field === 'term') {
            value = parseInt(value) || 1;
        } else if (field === 'interestRate') {
            value = parseFloat(value) || 0;
        }

        setFinancialData(prev => ({ ...prev, [field]: value }));
    };

    // Generate payment plan when data changes
    useEffect(() => {
        if (vehicleData.price > 0 && financialData.term > 0 && senetBaslangicTarihi) {
            const plan = generatePaymentPlan();
            setPaymentPlan(plan);
        }
    }, [vehicleData.price, financialData.downPayment, financialData.term, financialData.interestRate, senetBaslangicTarihi]);

    // Generate and show payment table
    const handleGeneratePaymentPlan = () => {
        if (vehicleData.price <= 0 || financialData.term <= 0 || !senetBaslangicTarihi) {
            setError('Lütfen araç bedeli, vade ve senet başlangıç tarihini giriniz');
            return;
        }

        const plan = generatePaymentPlan();
        setPaymentPlan(plan);
        setError('');
        setSuccess('Ödeme planı oluşturuldu. Tarih ve tutarları düzenleyebilirsiniz.');
    };

    // Update payment plan item
    const updatePaymentPlanItem = (index, field, value) => {
        const newPlan = [...paymentPlan];
        if (field === 'taksitTutari') {
            newPlan[index][field] = parseInt(value) || 0;
        } else {
            newPlan[index][field] = value;
        }
        setPaymentPlan(newPlan);
    };

    // Submit standalone senet
    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // Validation
            const errors = [];

            if (!customerData.name || customerData.name.trim().length < 2) {
                errors.push('Müşteri adı gereklidir');
            }

            if (!customerData.phone || !validatePhoneNumber(customerData.phone)) {
                errors.push('Geçerli telefon numarası gereklidir');
            }

            if (!customerData.tcNo) {
                errors.push('Kimlik numarası gereklidir');
            }

            if (!vehicleData.plate || vehicleData.plate.trim().length < 5) {
                errors.push('Plaka gereklidir');
            }

            if (!vehicleData.price || vehicleData.price <= 0) {
                errors.push('Araç fiyatı gereklidir');
            }

            if (paymentPlan.length === 0) {
                errors.push('Ödeme planı oluşturulmamış');
            }

            if (hasGuarantor) {
                if (!guarantorData.name || guarantorData.name.trim().length < 2) {
                    errors.push('Kefil adı gereklidir');
                }
                if (!guarantorData.tcNo) {
                    errors.push('Kefil kimlik numarası gereklidir');
                }
            }

            if (errors.length > 0) {
                setError(errors.join(', '));
                setLoading(false);
                return;
            }

            const requestData = {
                customerData: {
                    ...customerData,
                    address: customerData.address || ''
                },
                identityType,
                vehicleData,
                financialData,
                paymentPlan: paymentPlan,  // Edit edilmiş payment plan'i gönder
                senetBaslangicTarihi,
                guarantorData: hasGuarantor ? guarantorData : undefined,
                hasGuarantor
            };

            const response = await api.post('/reports/standalone-senet', requestData);

            if (response.data.success) {
                setSuccess('Sözleşmesiz senet başarıyla oluşturuldu!');

                // Senet sayfasını aç
                const reportId = response.data.report._id;
                const token = localStorage.getItem('token');
                const senetUrl = `${process.env.REACT_APP_API_URL}/reports/${reportId}/senet?token=${token}`;
                window.open(senetUrl, '_blank', 'noopener,noreferrer');

                setTimeout(() => {
                    navigate('/standalone-senets');
                }, 2000);
            }

        } catch (error) {
            console.error('Senet oluşturma hatası:', error);
            setError(error.response?.data?.message || 'Senet oluşturulurken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-5">
            <Row className="mb-4">
                <Col>
                    <h1 className="text-center">Sözleşmesiz Senet Oluştur</h1>
                    <p className="text-center text-muted">
                        Sadece senet düzenleme için hızlı form
                    </p>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Form onSubmit={(e) => e.preventDefault()}>
                <Row>
                    {/* Müşteri Bilgileri */}
                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">Müşteri Bilgileri</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ad Soyad *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={customerData.name}
                                        onChange={(e) => handleCustomerChange('name', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Telefon *</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        value={customerData.phone}
                                        onChange={(e) => handleCustomerChange('phone', e.target.value)}
                                        className={phoneError ? "is-invalid" : ""}
                                        placeholder="05XXXXXXXXX"
                                        required
                                    />
                                    {phoneError && <div className="invalid-feedback">{phoneError}</div>}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={customerData.email}
                                        onChange={(e) => handleCustomerChange('email', e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Kimlik Türü *</Form.Label>
                                    <div className="d-flex gap-3 mt-2">
                                        <Form.Check
                                            type="radio"
                                            id="customer-tcknn"
                                            name="identityType"
                                            label="T.C. Kimlik No"
                                            checked={identityType === 'TCKN'}
                                            onChange={() => {
                                                setIdentityType('TCKN');
                                                setCustomerData(prev => ({ ...prev, tcNo: '' }));
                                                setIdentityError('');
                                            }}
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="customer-vkn"
                                            name="identityType"
                                            label="Vergi Kimlik No"
                                            checked={identityType === 'VKN'}
                                            onChange={() => {
                                                setIdentityType('VKN');
                                                setCustomerData(prev => ({ ...prev, tcNo: '' }));
                                                setIdentityError('');
                                            }}
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {identityType === 'TCKN' ? 'T.C. Kimlik No' : 'Vergi Kimlik No'} *
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={customerData.tcNo}
                                        onChange={(e) => handleCustomerChange('tcNo', e.target.value)}
                                        maxLength={identityType === 'TCKN' ? 11 : 10}
                                        placeholder={identityType === 'TCKN' ? '12345678901' : '1234567890'}
                                        className={identityError ? "is-invalid" : ""}
                                        required
                                    />
                                    {identityError && <div className="invalid-feedback">{identityError}</div>}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Adres</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={customerData.address}
                                        onChange={(e) => handleCustomerChange('address', e.target.value)}
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        {/* Kefil Bilgileri */}
                        <Card className="mb-4">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">Kefil Bilgileri</h6>
                                <Form.Check
                                    type="switch"
                                    id="kefil-switch"

                                    label="Kefil var"
                                    checked={hasGuarantor}
                                    onChange={(e) => {
                                        setHasGuarantor(e.target.checked);
                                        if (!e.target.checked) {
                                            setGuarantorData({ name: '', tcNo: '', address: '', identityType: 'TCKN' });
                                            setGuarantorError('');
                                        }
                                    }}
                                />
                            </Card.Header>
                            {hasGuarantor && (
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Kefil Kimlik Türü</Form.Label>
                                        <div className="d-flex gap-3 mt-2">
                                            <Form.Check
                                                type="radio"
                                                id="guarantor-tckn"
                                                name="guarantorIdentityType"
                                                label="T.C. Kimlik No"
                                                checked={guarantorData.identityType === 'TCKN'}
                                                onChange={() => {
                                                    setGuarantorData(prev => ({ ...prev, identityType: 'TCKN', tcNo: '' }));
                                                    setGuarantorError('');
                                                }}
                                            />
                                            <Form.Check
                                                type="radio"
                                                id="guarantor-vkn"
                                                name="guarantorIdentityType"    
                                                label="Vergi Kimlik No"
                                                checked={guarantorData.identityType === 'VKN'}
                                                onChange={() => {
                                                    setGuarantorData(prev => ({ ...prev, identityType: 'VKN', tcNo: '' }));
                                                    setGuarantorError('');
                                                }}
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Kefil Adı *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={guarantorData.name}
                                            onChange={(e) => handleGuarantorChange('name', e.target.value)}
                                            required={hasGuarantor}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {guarantorData.identityType === 'TCKN' ? 'T.C. Kimlik No' : 'Vergi Kimlik No'} *
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={guarantorData.tcNo}
                                            onChange={(e) => handleGuarantorChange('tcNo', e.target.value)}
                                            maxLength={guarantorData.identityType === 'TCKN' ? 11 : 10}
                                            className={guarantorError ? "is-invalid" : ""}
                                            required={hasGuarantor}
                                        />
                                        {guarantorError && <div className="invalid-feedback">{guarantorError}</div>}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Kefil Adresi</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={guarantorData.address}
                                            onChange={(e) => handleGuarantorChange('address', e.target.value)}
                                        />
                                    </Form.Group>
                                </Card.Body>
                            )}
                        </Card>
                    </Col>

                    {/* Araç Bilgileri */}
                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">Araç Bilgileri (Stok)</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Plaka *</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={vehicleData.plate}
                                            onChange={(e) => handleVehicleChange('plate', e.target.value)}
                                            placeholder="34ABC123"
                                            className={plateSearchError ? "is-invalid" : ""}
                                            required
                                        />
                                        <Button
                                            variant="outline-primary"
                                            onClick={handleSearchByPlate}
                                            disabled={vehicleData.plate.length < 5 || searchingByPlate}
                                        >
                                            {searchingByPlate ? <Spinner size="sm" /> : <FaSearch />}
                                        </Button>
                                    </div>
                                    {plateSearchError && <div className="invalid-feedback">{plateSearchError}</div>}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Marka</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={vehicleData.make}
                                        onChange={(e) => handleVehicleChange('make', e.target.value)}
                                        disabled={foundVehicle}
                                        className={foundVehicle ? 'bg-light' : ''}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Model</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={vehicleData.model}
                                        onChange={(e) => handleVehicleChange('model', e.target.value)}
                                        disabled={foundVehicle}
                                        className={foundVehicle ? 'bg-light' : ''}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Araç Bedeli (TL) *</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">₺</span>
                                        <Form.Control
                                            type="text"
                                            value={formattedPrice}
                                            onChange={(e) => handleFormattedNumberChange('price', e.target.value)}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                </Form.Group>

                                {foundVehicle && (
                                    <Alert variant="info">
                                        <strong>Araç Bulundu:</strong><br />
                                        {foundVehicle.brand} {foundVehicle.model}<br />
                                        <small>Status: {foundVehicle.status?.toUpperCase()}</small>
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Finansman & Ödeme Planı */}
                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">Finansman Detayları</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Peşinat (TL)</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">₺</span>
                                        <Form.Control
                                            type="text"
                                            value={formattedDownPayment}
                                            onChange={(e) => handleFormattedNumberChange('downPayment', e.target.value)}
                                            placeholder="0"
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Vade (Ay) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max="60"
                                        value={financialData.term}
                                        onChange={(e) => handleFinancialChange('term', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Aylık Faiz Oranı (%) *</Form.Label>
                                    <div className="input-group">
                                        <Form.Control
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            max="50"
                                            value={financialData.interestRate}
                                            onChange={(e) => handleFinancialChange('interestRate', e.target.value)}
                                            required
                                        />
                                        <span className="input-group-text">%</span>
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Senet Başlangıç Tarihi *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={senetBaslangicTarihi}
                                        onChange={(e) => setSenetBaslangicTarihi(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        required
                                    />
                                </Form.Group>

                                {paymentPlan.length > 0 && (
                                    <Card bg="light" className="mt-3">
                                        <Card.Body>
                                            <h6 className="text-primary mb-3">
                                                <FaCalculator className="me-2" />
                                                Ödeme Planı Özeti
                                            </h6>
                                            <div>
                                                <small className="text-muted">Kredi Tutarı:</small>
                                                <p className="mb-2 fw-bold">
                                                    {formatNumber(vehicleData.price - financialData.downPayment)} TL
                                                </p>
                                            </div>
                                            <div>
                                                <small className="text-muted">Taksit Sayısı:</small>
                                                <p className="mb-2 fw-bold">{paymentPlan.length} Ay</p>
                                            </div>
                                            <div>
                                                <small className="text-muted">Toplam Ödeme:</small>
                                                <p className="mb-2 fw-bold">
                                                    {formatNumber(paymentPlan.reduce((sum, item) => sum + item.taksitTutari, 0))} TL
                                                </p>
                                            </div>
                                            <Button
                                                variant="info"
                                                size="sm"
                                                onClick={handleGeneratePaymentPlan}
                                                className="w-100"
                                            >
                                                <FaEdit className="me-2" />
                                                Ödeme Planı Oluştur
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {/* Ödeme Planı Tablosu - Tam Genişlik */}
                {paymentPlan.length > 0 && (
                    <Row className="mb-4">
                        <Col>
                            <Card>
                                <Card.Header>
                                    <h5 className="mb-0">
                                        <FaEdit className="me-2" />
                                        Ödeme Planı - Düzenle
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="table-responsive">
                                        <Table striped bordered hover>
                                            <thead className="table-dark">
                                                <tr>
                                                    <th>Müşteri</th>
                                                    <th>Taksit No</th>
                                                    <th>Vade Tarihi</th>
                                                    <th>Taksit Tutarı (TL)</th>
                                                    <th>Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paymentPlan.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <strong>{customerData.name || 'Müşteri'}</strong>
                                                            <br />
                                                            <small className="text-muted">{customerData.tcNo}</small>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="primary">{item.taksit}/{paymentPlan.length}</Badge>
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="date"
                                                                value={item.taksitTarihi}
                                                                onChange={(e) => updatePaymentPlanItem(index, 'taksitTarihi', e.target.value)}
                                                                className="form-control-sm"
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className="input-group input-group-sm">
                                                                <span className="input-group-text">₺</span>
                                                                <Form.Control
                                                                    type="number"
                                                                    value={item.taksitTutari}
                                                                    onChange={(e) => updatePaymentPlanItem(index, 'taksitTutari', e.target.value)}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="warning">Bekliyor</Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <Alert variant="info" className="mt-3">
                                        <strong>💰 Toplam Ödeme:</strong> {formatNumber(paymentPlan.reduce((sum, item) => sum + item.taksitTutari, 0))} TL
                                    </Alert>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col className="text-center">
                        <Button
                            variant="success"
                            size="lg"
                            disabled={loading || paymentPlan.length === 0}
                            onClick={() => setShowConfirmModal(true)}
                            className="px-5"
                        >
                            {loading ? 'Oluşturuluyor...' : (
                                <>
                                    <FaFileContract className="me-2" />
                                    Senet Oluştur
                                </>
                            )}
                        </Button>
                    </Col>
                </Row>
            </Form>

            {/* Confirm Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Senet Oluştur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Bu sözleşmesiz senet oluşturulacak:</p>
                    <ul>
                        <li><strong>Müşteri:</strong> {customerData.name}</li>
                        <li><strong>Araç:</strong> {vehicleData.make} {vehicleData.model}</li>
                        <li><strong>Taksit Sayısı:</strong> {paymentPlan.length} ay</li>
                        <li><strong>Toplam Tutar:</strong> {formatNumber(paymentPlan.reduce((sum, item) => sum + item.taksitTutari, 0))} TL</li>
                    </ul>
                    <p className="text-muted">Onaylıyor musunuz?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        İptal
                    </Button>
                    <Button variant="success" onClick={() => { setShowConfirmModal(false); handleSubmit(); }} disabled={loading}>
                        {loading ? 'Oluşturuluyor...' : 'Oluştur'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default StandaloneSenetPage;